/**
 * Identidade geográfica estruturada (módulo puro, sem rede e sem custo).
 *
 * Problema resolvido: os geocoders devolvem frequentemente o MUNICÍPIO em vez
 * da localidade pedida (Vidago → Chaves, Sintra → Lisboa, Mestre → Veneza,
 * Brooklyn → New York). A partir daqui, a localidade pedida pelo utilizador é
 * preservada separadamente do município e nunca é substituída em silêncio.
 *
 * Não existe hardcode por país: são regras de níveis administrativos,
 * aplicáveis a vilas, aldeias, freguesias, bairros e ilhas em todo o mundo.
 */

import { comparableCityName } from "./city-endonym";

export type GeoIdentityLevel =
  | "locality"
  | "sublocality"
  | "parish"
  | "municipality"
  | "region"
  | "country"
  | "unknown";

export type GeoViewport = {
  north: number;
  south: number;
  east: number;
  west: number;
};

export type GeoIdentity = {
  /** Exatamente o que o utilizador pediu (nunca reescrito). */
  requestedName: string;
  /** Nome a usar em títulos, pesquisas e validação. */
  displayName: string;
  /** Nível administrativo do `displayName`. */
  level: GeoIdentityLevel;
  locality?: string;
  sublocality?: string;
  parish?: string;
  municipality?: string;
  region?: string;
  country?: string;
  countryCode?: string;
  lat?: number;
  lng?: number;
  viewport?: GeoViewport;
  /** O geocoder tentou substituir a localidade pedida por outra unidade. */
  municipalitySubstituted: boolean;
};

export type GeoSearchMode = "locality" | "surroundings";

export const fold = (value: string) =>
  (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/gu, "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();

export const sameGeoName = (a?: string | null, b?: string | null) =>
  !!a && !!b && comparableCityName(a) === comparableCityName(b);

// ---------------------------------------------------------------------------
// Construção a partir dos geocoders
// ---------------------------------------------------------------------------

export type GoogleComponent = {
  long_name?: string;
  short_name?: string;
  types?: string[];
};

const pick = (components: GoogleComponent[], type: string) =>
  components.find((component) => component.types?.includes(type))?.long_name || undefined;

export type GeoIdentityParts = {
  locality?: string;
  sublocality?: string;
  parish?: string;
  municipality?: string;
  region?: string;
  country?: string;
  countryCode?: string;
};

export function partsFromGoogleComponents(
  components: GoogleComponent[] | null | undefined,
): GeoIdentityParts {
  const list = components ?? [];
  const countryComponent = list.find((component) => component.types?.includes("country"));
  return {
    locality: pick(list, "locality") || pick(list, "postal_town"),
    sublocality:
      pick(list, "sublocality") ||
      pick(list, "sublocality_level_1") ||
      pick(list, "neighborhood"),
    parish: pick(list, "administrative_area_level_4") || pick(list, "administrative_area_level_3"),
    municipality: pick(list, "administrative_area_level_2"),
    region: pick(list, "administrative_area_level_1"),
    country: countryComponent?.long_name,
    countryCode: (countryComponent?.short_name || "").toUpperCase() || undefined,
  };
}

export type NominatimAddressParts = Record<string, string | undefined>;

export function partsFromNominatimAddress(
  address: NominatimAddressParts | null | undefined,
): GeoIdentityParts {
  const a = address ?? {};
  return {
    locality: a.city || a.town || a.village || a.hamlet,
    sublocality: a.suburb || a.neighbourhood || a.quarter || a.borough,
    parish: a.civil_parish || a.parish || a.city_district,
    municipality: a.municipality || a.county,
    region: a.state || a.region,
    country: a.country,
    countryCode: (a.country_code || "").toUpperCase() || undefined,
  };
}

function levelOfMatch(requested: string, parts: GeoIdentityParts): GeoIdentityLevel | null {
  if (sameGeoName(requested, parts.locality)) return "locality";
  if (sameGeoName(requested, parts.sublocality)) return "sublocality";
  if (sameGeoName(requested, parts.parish)) return "parish";
  if (sameGeoName(requested, parts.municipality)) return "municipality";
  if (sameGeoName(requested, parts.region)) return "region";
  if (sameGeoName(requested, parts.country)) return "country";
  return null;
}

/**
 * Constrói a identidade preservando SEMPRE o nome pedido como centro da rota.
 * Quando o geocoder devolve outro nome (tipicamente o município), guardamos
 * essa informação em `municipality`/`region` e sinalizamos a substituição —
 * mas o `displayName` continua a ser a localidade pedida.
 */
export function buildGeoIdentity(input: {
  requestedName: string;
  parts?: GeoIdentityParts;
  lat?: number;
  lng?: number;
  viewport?: GeoViewport;
}): GeoIdentity {
  const requested = (input.requestedName ?? "").trim();
  const parts = input.parts ?? {};
  const matched = requested ? levelOfMatch(requested, parts) : null;

  // O geocoder devolveu uma unidade diferente da pedida (Vidago → Chaves).
  const geocoderName = parts.locality || parts.municipality || parts.region;
  const substituted =
    !!requested && !!geocoderName && !matched && !sameGeoName(requested, geocoderName);

  const level: GeoIdentityLevel = matched ?? (requested ? "locality" : "unknown");

  return {
    requestedName: requested,
    // Regra dura: o nome pedido nunca é trocado pelo município.
    displayName: requested || geocoderName || "",
    level,
    locality:
      matched === "locality" || matched === "parish" || matched === "sublocality" || substituted
        ? requested || parts.locality
        : parts.locality,
    sublocality: parts.sublocality,
    parish: parts.parish,
    municipality: parts.municipality,
    region: parts.region,
    country: parts.country,
    countryCode: parts.countryCode,
    lat: input.lat,
    lng: input.lng,
    viewport: input.viewport,
    municipalitySubstituted: substituted,
  };
}

// ---------------------------------------------------------------------------
// Pesquisa e validação
// ---------------------------------------------------------------------------

/** Raio local adequado ao nível: bairros/aldeias pequenos, regiões maiores. */
export function localRadiusMeters(identity: GeoIdentity, mode: GeoSearchMode = "locality"): number {
  if (mode === "surroundings") return 30_000;
  switch (identity.level) {
    case "sublocality":
      return 4_000;
    case "parish":
    case "locality":
      return 8_000;
    case "municipality":
      return 15_000;
    case "region":
      return 40_000;
    default:
      return 10_000;
  }
}

/** Texto de pesquisa: refere sempre a localidade pedida, com contexto. */
export function placesSearchScope(identity: GeoIdentity, mode: GeoSearchMode = "locality"): string {
  const context =
    mode === "surroundings"
      ? [identity.municipality, identity.region, identity.country]
      : [identity.municipality, identity.country];
  const parts = [identity.displayName, ...context.filter(Boolean)];
  const unique: string[] = [];
  for (const part of parts) {
    if (part && !unique.some((existing) => sameGeoName(existing, part))) unique.push(part);
  }
  return unique.join(", ");
}

export function isInsideViewport(
  point: { lat: number; lng: number },
  viewport?: GeoViewport,
): boolean {
  if (!viewport) return false;
  return (
    point.lat <= viewport.north &&
    point.lat >= viewport.south &&
    point.lng <= viewport.east &&
    point.lng >= viewport.west
  );
}

function haversineMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

export function isWithinLocality(
  point: { lat: number; lng: number },
  identity: GeoIdentity,
  mode: GeoSearchMode = "locality",
): boolean {
  if (isInsideViewport(point, identity.viewport)) return true;
  if (!Number.isFinite(identity.lat) || !Number.isFinite(identity.lng)) return true;
  return (
    haversineMeters(point, { lat: identity.lat!, lng: identity.lng! }) <=
    localRadiusMeters(identity, mode)
  );
}

/** A maioria das paragens tem de pertencer à localidade (ou ao raio aceite). */
export function validateLocalityCoverage(
  stops: Array<{ name: string; lat?: number; lng?: number }>,
  identity: GeoIdentity,
  mode: GeoSearchMode = "locality",
): { valid: boolean; inside: number; total: number; outside: string[] } {
  const located = stops.filter(
    (stop) => Number.isFinite(stop.lat) && Number.isFinite(stop.lng),
  ) as Array<{ name: string; lat: number; lng: number }>;
  const outside = located
    .filter((stop) => !isWithinLocality(stop, identity, mode))
    .map((stop) => stop.name);
  const inside = located.length - outside.length;
  return {
    valid: located.length === 0 || inside * 2 > located.length,
    inside,
    total: located.length,
    outside,
  };
}

// ---------------------------------------------------------------------------
// Pergunta gratuita quando há poucas opções locais
// ---------------------------------------------------------------------------

export type LocalityExpansionQuestion = {
  message: string;
  keepLabel: string;
  expandLabel: string;
  hint: string;
};

const EXPANSION_COPY: Record<
  string,
  {
    message: (locality: string, around: string) => string;
    keep: (locality: string) => string;
    expand: (around: string) => string;
    hint: string;
  }
> = {
  pt: {
    message: (locality, around) =>
      `Encontrei poucas opções em ${locality}. Queres manter a rota em ${locality} ou alargar às proximidades de ${around}?`,
    keep: (locality) => `Manter em ${locality}`,
    expand: (around) => `Alargar às proximidades de ${around}`,
    hint: "Ainda não usámos créditos — só queremos acertar no âmbito.",
  },
  en: {
    message: (locality, around) =>
      `I found few options in ${locality}. Keep the route in ${locality} or widen it to the ${around} area?`,
    keep: (locality) => `Keep it in ${locality}`,
    expand: (around) => `Widen to ${around}`,
    hint: "No credits used yet — we just want the right scope.",
  },
  es: {
    message: (locality, around) =>
      `Encontré pocas opciones en ${locality}. ¿Mantener la ruta en ${locality} o ampliar a los alrededores de ${around}?`,
    keep: (locality) => `Mantener en ${locality}`,
    expand: (around) => `Ampliar a ${around}`,
    hint: "Aún no usamos créditos — solo queremos acertar el ámbito.",
  },
  fr: {
    message: (locality, around) =>
      `J'ai trouvé peu d'options à ${locality}. Garder l'itinéraire à ${locality} ou l'élargir aux environs de ${around} ?`,
    keep: (locality) => `Garder à ${locality}`,
    expand: (around) => `Élargir à ${around}`,
    hint: "Aucun crédit utilisé — nous voulons juste le bon périmètre.",
  },
  de: {
    message: (locality, around) =>
      `Ich habe wenige Optionen in ${locality} gefunden. Route in ${locality} behalten oder auf die Umgebung von ${around} erweitern?`,
    keep: (locality) => `In ${locality} bleiben`,
    expand: (around) => `Auf ${around} erweitern`,
    hint: "Noch kein Guthaben verbraucht — wir wollen nur den richtigen Umfang.",
  },
};

/** `null` quando há resultados suficientes: nunca perguntamos sem motivo. */
export function localityExpansionQuestion(
  identity: GeoIdentity,
  resultCount: number,
  options?: { language?: string; minimum?: number },
): LocalityExpansionQuestion | null {
  const minimum = options?.minimum ?? 6;
  if (resultCount >= minimum) return null;
  const around = identity.municipality || identity.region || identity.country;
  if (!around || sameGeoName(around, identity.displayName)) return null;
  const copy = EXPANSION_COPY[options?.language ?? "pt"] ?? EXPANSION_COPY.en;
  return {
    message: copy.message(identity.displayName, around),
    keepLabel: copy.keep(identity.displayName),
    expandLabel: copy.expand(around),
    hint: copy.hint,
  };
}

/** Só alargamos quando o utilizador escolheu explicitamente os arredores. */
export function resolveSearchMode(choice?: "keep" | "expand" | null): GeoSearchMode {
  return choice === "expand" ? "surroundings" : "locality";
}

// ---------------------------------------------------------------------------
// Localidade explícita no texto livre ("Estou em Vidago")
// ---------------------------------------------------------------------------

/**
 * Frases genéricas (PT/EN/ES/FR/IT/DE) que indicam que o utilizador já disse
 * onde está. Não há lista de cidades: qualquer localidade nomeada aqui conta
 * como destino explícito e nunca deve ser trocada pelo município nem
 * perguntada de novo.
 */
const EXPLICIT_LOCALITY_PATTERNS: RegExp[] = [
  /\b(?:estou|estamos|encontro-me|encontramo-nos|j[aá]\s+cheguei|acabei\s+de\s+chegar)\s+(?:em|no|na)\s+([\p{L}][\p{L}\p{N}'’.\- ]{1,60})/iu,
  /\b(?:i\s*am|i'm|we\s*are|we're)\s+(?:in|at)\s+([\p{L}][\p{L}\p{N}'’.\- ]{1,60})/iu,
  /\b(?:estoy|estamos)\s+en\s+([\p{L}][\p{L}\p{N}'’.\- ]{1,60})/iu,
  /\b(?:je\s+suis|nous\s+sommes)\s+[aà]\s+([\p{L}][\p{L}\p{N}'’.\- ]{1,60})/iu,
  /\b(?:sono|siamo)\s+a\s+([\p{L}][\p{L}\p{N}'’.\- ]{1,60})/iu,
  /\bich\s+bin\s+in\s+([\p{L}][\p{L}\p{N}'’.\- ]{1,60})/iu,
];

/**
 * Corta uma duração escrita no fim de um nome de localidade ("Sintra 1 dia",
 * "Ponte de Lima durante 3 horas", "Rio de Janeiro por 2 dias", "Sintra for 1
 * day"). É a ÚNICA função de limpeza de duração da aplicação: todos os
 * fluxos (extração normal, Descoberta, Premium, Pro e geocoding) devem
 * reutilizá-la para o comportamento não divergir entre ramos.
 *
 * Nunca remove partes do nome: só corta a partir do número.
 */
const DURATION_TAIL =
  /\s*(?:\b(?:durante|por|para|em|de|for|during|pendant|pour|per|f[uü]r|fuer|nach)\s+)?\d{1,3}(?:[.,]\d+)?\s*(?:h|hs|hrs?|horas?|hours?|heures?|ore|stunden?|dias?|days?|jours?|giorni|giorno|tage|tagen|noites?|nights?|nuits?|notti|n[aä]chte|semanas?|weeks?|semaines?|settimane|wochen)\b.*$/iu;

export function stripDurationSuffix(value: string): string {
  return (value ?? "").replace(DURATION_TAIL, "").trim();
}

const trimExplicitLocalityTail = (value: string) =>
  stripDurationSuffix(value)
    .replace(/\s*[,;].*$/u, "")
    .replace(/\s+\b(?:e|and|y|et|und|hoje|amanh[aã]|today|tomorrow|com|with|avec|mit|perto|pr[oó]xim[oa]|near|close|looking|procurando|à|a|and\s+i|querendo).*$/iu, "")
    .replace(/[.,;:!?]+$/u, "")
    .trim();


/**
 * Extrai a localidade que o utilizador já indicou explicitamente no texto
 * livre. Devolve `undefined` quando não há nenhuma menção clara — nesse caso
 * o fluxo pode continuar a perguntar normalmente.
 */
export function extractExplicitLocality(prompt: string): string | undefined {
  const text = (prompt ?? "").trim();
  if (!text) return undefined;
  for (const pattern of EXPLICIT_LOCALITY_PATTERNS) {
    const match = text.match(pattern)?.[1];
    if (!match) continue;
    const cleaned = trimExplicitLocalityTail(match);
    if (cleaned.length >= 2 && cleaned.length <= 80) return cleaned;
  }
  return undefined;
}

